import { ActionBar } from '@/components/layout/ActionBar';
import { Button } from '@/components/ui/button';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useBulkValidatePaperScreeningsService } from '@/services/literatureReviews';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';

interface ScreeningActionBarProps<T extends { id: string }> {
  open: boolean;
  clearSelection: () => void;
  validatableRows: T[];
  selectedRowsCount: number;
  displayInconclusiveWarning?: boolean;
}

export function ScreeningActionBar<T extends { id: string }>({
  open,
  clearSelection,
  validatableRows,
  selectedRowsCount,
  displayInconclusiveWarning = false,
}: ScreeningActionBarProps<T>) {
  const { bulkValidatePaperScreenings, isPending } =
    useBulkValidatePaperScreeningsService();

  const handleValidateAll = async () => {
    try {
      const paperScreeningIds = validatableRows.map(row => row.id);
      await bulkValidatePaperScreenings(paperScreeningIds);
      toast.success('Paper screenings validated successfully');
      clearSelection();
    } catch (error) {
      toast.error('Failed to validate paper screenings');
    }
  };

  const handleClose = () => {
    clearSelection();
  };

  return (
    <ActionBar open={open}>
      <FlexRow gap="6" alignItems="center" justifyContent="between" w="full">
        <FlexRow gap="2" alignItems="center">
          <Button variant="link" size="icon" onClick={handleClose}>
            <X className="text-white" />
          </Button>
          <Text variant="body-sm" className="text-white">
            {selectedRowsCount} rows selected
          </Text>
        </FlexRow>
        <FlexRow gap="2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                onClick={handleValidateAll}
                loading={isPending}
              >
                <Check />
                Validate All
              </Button>
            </TooltipTrigger>
            {displayInconclusiveWarning && (
              <TooltipContent sideOffset={10}>
                Some paper screenings are inconclusive and cannot be validated.
              </TooltipContent>
            )}
          </Tooltip>
        </FlexRow>
      </FlexRow>
    </ActionBar>
  );
}
